import {
  type DeparturePattern,
  diffTimes,
  expandPattern,
  fromMinutes,
  sortTimes,
  stationTimes,
  toMinutes,
} from '@trafiku/contracts';
import { ArrowRight, Copy, History, Plus, Ruler, Sparkles, TriangleAlert, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { Button } from '@/components/ui/button';
import { Input, TagInput } from '@/components/ui/form';
import { Card, PageHeader, Spinner } from '@/components/ui/layout';
import { useAuth } from '@/lib/auth';
import { safeHex, textOn } from '@/lib/colors';
import { t, tn } from '@/lib/i18n';
import { useNetwork } from '@/lib/network';
import { usePerformance } from '@/lib/performance';
import {
  type Day,
  type Draft,
  estimateMinutes,
  fillMinutes,
  initialDraft,
  minutesFromCurrent,
  useLineTimetable,
  useSaveLineTimetable,
} from '@/lib/timetables';
import { travelOrdered } from '@/lib/zone-check';

const isTime = (v: string) => /^([01]\d|2[0-3]):[0-5]\d$/.test(v);
const normaliseTime = (v: string) => {
  const m = v.match(/^(\d{1,2})[:.]?(\d{2})$/);
  return m ? `${m[1]!.padStart(2, '0')}:${m[2]}` : v;
};
const DAYS: Day[] = ['week', 'weekend'];
const DAY_LABEL: Record<Day, string> = { week: t('Weekdays'), weekend: t('Weekend') };

/**
 * A line's timetable in one place: when buses leave the first station (rules like "every 15
 * minutes" and/or a list) and how long they take to each station. Every station's times follow,
 * with a preview of what changes before saving.
 */
export function LineTimetablePage() {
  const { lineId } = useParams();
  const data = useLineTimetable(lineId);
  const network = useNetwork();
  const save = useSaveLineTimetable(lineId!);
  const navigate = useNavigate();
  const { can } = useAuth();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [day, setDay] = useState<Day>('week');
  const [kmh, setKmh] = useState(20);
  // Measured times between stops (last 30 days, the kind of day being edited).
  const history = usePerformance(lineId, 30, day);

  const netLine = network.data?.lines.find((l) => l._id === lineId);
  const route = useMemo(() => {
    if (!netLine || !network.data) return undefined;
    return travelOrdered(netLine, new Map(network.data.stations.map((s) => [s._id, s])));
  }, [netLine, network.data]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: start once per loaded line
  useEffect(() => {
    if (data.data && network.data && !draft) setDraft(initialDraft(data.data, route));
  }, [data.data, network.data]);

  if (!data.data || !network.data || !draft) return <Spinner />;
  const { line, current } = data.data;
  const stations = line.stations;
  const first = stations[0];
  const last = stations.at(-1);
  const zone = network.data.zones.find((z) => z.on_line_going === lineId || z.on_line_return === lineId);
  const pairedId = zone
    ? zone.on_line_going === lineId
      ? zone.on_line_return
      : zone.on_line_going
    : undefined;
  const paired = network.data.lines.find((l) => l._id === pairedId);
  const color = safeHex(zone?.color ?? netLine?.color, '#0b3f82');

  const plan = draft[day];
  const setPlan = (p: Partial<typeof plan>) => setDraft({ ...draft, [day]: { ...plan, ...p } });
  const setMinutes = (m: Record<string, number | null>) => setDraft({ ...draft, minutes: m });
  const missing = stations.filter((s) => draft.minutes[s._id] == null);
  const backwards = stations.filter(
    (s, i) => i > 0 && (draft.minutes[s._id] ?? 0) < (draft.minutes[stations[i - 1]!._id] ?? 0),
  );

  // What each station gets, for both kinds of day, against what it has now.
  const result = stations.map((s) => {
    const m = draft.minutes[s._id] ?? 0;
    const after = {
      week: stationTimes(draft.week.departures, m),
      weekend: stationTimes(draft.weekend.departures, m),
    };
    const before = current[s._id] ?? { week: [], weekend: [] };
    const d = { week: diffTimes(before.week, after.week), weekend: diffTimes(before.weekend, after.weekend) };
    const changes =
      d.week.added.length + d.week.removed.length + d.weekend.added.length + d.weekend.removed.length;
    return { station: s, minutes: m, after, before, diff: d, changes };
  });
  const changedStations = result.filter((r) => r.changes > 0).length;

  const submit = async () => {
    await save.mutateAsync({
      week: draft.week,
      weekend: draft.weekend,
      offsets: stations.map((s) => ({ station: s._id, minutes: draft.minutes[s._id] ?? 0 })),
    });
    navigate('/schedules');
  };

  return (
    <>
      <PageHeader
        title={
          <span className="flex items-center gap-2">
            {zone && (
              <span
                className="inline-flex h-6 min-w-6 items-center justify-center rounded-full px-1.5 text-xs font-bold"
                style={{ background: color, color: textOn(color) }}
              >
                {zone.bus_sign}
              </span>
            )}
            {t('Timetable: {line}', { line: line.name })}
          </span>
        }
        subtitle={
          first && last ? (
            <span className="flex items-center gap-1">
              {first.name} <ArrowRight className="size-3.5" /> {last.name} ·{' '}
              {tn(stations.length, '{n} station', '{n} stations')}
            </span>
          ) : undefined
        }
        back="/schedules"
        actions={
          paired && (
            <Link
              to={`/schedules/directions/${paired._id}`}
              className="text-sm font-medium text-brand-700 hover:underline"
            >
              {t('Return direction: {line}', { line: paired.name })} →
            </Link>
          )
        }
      />

      <p className="mb-4 max-w-3xl text-sm text-slate-600">
        {t(
          'Set when buses leave {first} and how long they take to each station. The times at every station of this direction follow from that and are saved together.',
          { first: first?.name ?? '' },
        )}
      </p>

      <div className="grid gap-6 xl:grid-cols-2">
        {/* 1. Departures from the first station */}
        <Card
          title={t('1 · Departures from {station}', { station: first?.name ?? '' })}
          actions={
            <div className="flex rounded-md bg-slate-100 p-0.5 text-sm">
              {DAYS.map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setDay(d)}
                  className={`rounded px-3 py-1 ${day === d ? 'bg-white font-medium shadow-sm' : 'text-slate-600'}`}
                >
                  {DAY_LABEL[d]} <span className="text-xs text-slate-400">{draft[d].departures.length}</span>
                </button>
              ))}
            </div>
          }
        >
          <div className="space-y-4">
            <Patterns
              patterns={plan.patterns}
              onChange={(patterns) => setPlan({ patterns })}
              onApply={() =>
                setPlan({ departures: sortTimes(plan.patterns.flatMap((p) => expandPattern(p))) })
              }
            />
            <div>
              <div className="mb-1.5 flex items-center justify-between text-sm">
                <span className="font-medium text-slate-700">
                  {tn(plan.departures.length, '{n} departure', '{n} departures')}
                  {plan.departures.length > 0 && (
                    <span className="font-normal text-slate-500">
                      {' '}
                      · {plan.departures[0]}–{plan.departures.at(-1)}
                    </span>
                  )}
                </span>
                {day === 'weekend' && (
                  <Button
                    size="sm"
                    variant="secondary"
                    icon={<Copy className="size-4" />}
                    onClick={() => setPlan({ ...draft.week })}
                  >
                    {t('Same as weekdays')}
                  </Button>
                )}
              </div>
              <TagInput
                values={plan.departures}
                onChange={(v) => setPlan({ departures: sortTimes(v.map(normaliseTime).filter(isTime)) })}
                validate={(v) => isTime(normaliseTime(v))}
                placeholder={t('06:00 06:20 … (Enter, comma or paste)')}
              />
              <p className="mt-1 text-xs text-slate-500">
                {t('Add or remove single departures here, e.g. an extra early bus.')}
              </p>
            </div>
          </div>
        </Card>

        {/* 2. Travel times */}
        <Card
          title={t('2 · Travel time from {station}', { station: first?.name ?? '' })}
          actions={
            <div className="flex flex-wrap items-center gap-2">
              <Button
                size="sm"
                variant="secondary"
                icon={<Sparkles className="size-4" />}
                title={t('Read the travel times from the timetables saved now')}
                onClick={() =>
                  setMinutes(fillMinutes(stations, minutesFromCurrent(data.data!), draft.minutes))
                }
              >
                {t('From current timetables')}
              </Button>
              <Button
                size="sm"
                variant="secondary"
                icon={<History className="size-4" />}
                disabled={!history.data?.trips}
                title={
                  history.data?.trips
                    ? t('Usual travel times measured from the buses ({n} trips in the last 30 days)', {
                        n: history.data.trips,
                      })
                    : t('No trips recorded yet for this direction')
                }
                onClick={() => {
                  const known = Object.fromEntries(
                    (history.data?.offsets ?? []).filter((o) => o.known).map((o) => [o.station, o.minutes]),
                  );
                  setMinutes(fillMinutes(stations, known, draft.minutes));
                }}
              >
                {t('From bus history')}
              </Button>
              <span className="flex items-center gap-1 text-sm text-slate-600">
                <Button
                  size="sm"
                  variant="secondary"
                  icon={<Ruler className="size-4" />}
                  title={t('Distance along the route at this speed, plus half a minute per stop')}
                  onClick={() => setMinutes(estimateMinutes(route, stations, kmh))}
                >
                  {t('Estimate from route')}
                </Button>
                <Input
                  type="number"
                  min={5}
                  max={60}
                  className="w-16"
                  value={kmh}
                  onChange={(e) => setKmh(Number(e.target.value) || 20)}
                  aria-label={t('Average speed (km/h)')}
                />
                km/h
              </span>
            </div>
          }
          padded={false}
        >
          <div className="max-h-[420px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white text-left text-xs text-slate-500">
                <tr>
                  <th className="px-4 py-2 font-medium">{t('Station')}</th>
                  <th className="px-2 py-2 font-medium">{t('Minutes')}</th>
                  <th className="px-4 py-2 text-right font-medium">{t('From previous')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {stations.map((s, i) => {
                  const m = draft.minutes[s._id];
                  const prev = i > 0 ? draft.minutes[stations[i - 1]!._id] : null;
                  const gap = m != null && prev != null ? m - prev : null;
                  return (
                    <tr key={s._id}>
                      <td className="px-4 py-1.5">
                        <span className="flex items-center gap-2">
                          <span
                            className="inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full px-1 text-[11px] font-bold"
                            style={{ background: color, color: textOn(color) }}
                          >
                            {i + 1}
                          </span>
                          <span className="truncate">{s.name}</span>
                        </span>
                      </td>
                      <td className="px-2 py-1.5">
                        <Input
                          type="number"
                          min={0}
                          className="h-8 w-20"
                          disabled={i === 0}
                          value={m ?? ''}
                          invalid={m == null || (gap !== null && gap < 0)}
                          onChange={(e) =>
                            setMinutes({
                              ...draft.minutes,
                              [s._id]: e.target.value === '' ? null : Math.max(0, Number(e.target.value)),
                            })
                          }
                          aria-label={t('Minutes to {station}', { station: s.name })}
                        />
                      </td>
                      <td
                        className={`px-4 py-1.5 text-right text-xs ${gap !== null && gap < 0 ? 'text-red-600' : 'text-slate-500'}`}
                      >
                        {i === 0 ? t('start') : gap === null ? '—' : `+${gap} min`}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* 3. Result */}
      <Card
        className="mt-6"
        title={t('3 · Times at every station ({day})', { day: DAY_LABEL[day].toLowerCase() })}
        actions={
          <span className="flex items-center gap-3 text-xs text-slate-600">
            <span className="flex items-center gap-1">
              <span className="inline-block size-3 rounded-sm bg-emerald-100 ring-1 ring-emerald-300" />{' '}
              {t('new')}
            </span>
            <span>
              {tn(changedStations, '{n} station changes', '{n} stations change')} ·{' '}
              {tn(stations.length - changedStations, '{n} stays the same', '{n} stay the same')}
            </span>
          </span>
        }
        padded={false}
      >
        <ResultGrid result={result} day={day} departures={draft[day].departures} />
      </Card>

      <div className="sticky bottom-0 z-10 -mx-1 mt-6 flex flex-wrap items-center justify-end gap-3 border-t border-slate-200 bg-slate-50/95 px-1 py-3 backdrop-blur">
        {(missing.length > 0 || backwards.length > 0) && (
          <span className="mr-auto flex items-center gap-1.5 text-sm text-amber-700">
            <TriangleAlert className="size-4" />
            {missing.length > 0
              ? t('Set the travel time to every station first ({n} missing).', { n: missing.length })
              : t('Travel times go backwards at {station}.', { station: backwards[0]!.name })}
          </span>
        )}
        <Button variant="secondary" onClick={() => navigate('/schedules')}>
          {t('Cancel')}
        </Button>
        <Button
          loading={save.isPending}
          disabled={!can('admin', 'operator') || missing.length > 0}
          onClick={submit}
        >
          {t('Save timetables of {n} stations', { n: stations.length })}
        </Button>
      </div>
    </>
  );
}

/** "From 06:00 to 09:00 every 10 min" rules; Apply fills the departures from them. */
function Patterns({
  patterns,
  onChange,
  onApply,
}: {
  patterns: DeparturePattern[];
  onChange: (p: DeparturePattern[]) => void;
  onApply: () => void;
}) {
  const update = (i: number, p: Partial<DeparturePattern>) =>
    onChange(patterns.map((x, j) => (j === i ? { ...x, ...p } : x)));
  const valid = patterns.every((p) => isTime(p.from) && isTime(p.to) && p.to >= p.from && p.every >= 1);
  return (
    <div className="rounded-md bg-slate-50 p-3">
      <div className="mb-2 text-sm font-medium text-slate-700">{t('Rules')}</div>
      {patterns.length === 0 && (
        <p className="mb-2 text-xs text-slate-500">
          {t(
            'E.g. 06:00–09:00 every 10 min and 09:00–22:00 every 20 min, then "Fill departures from the rules".',
          )}
        </p>
      )}
      <div className="space-y-2">
        {patterns.map((p, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: rows have no identity
          <div key={i} className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
            {t('From')}
            <Input
              className="h-8 w-20 tabular-nums"
              placeholder="06:00"
              inputMode="numeric"
              value={p.from}
              invalid={!isTime(p.from)}
              onChange={(e) => update(i, { from: e.target.value })}
              onBlur={(e) => update(i, { from: normaliseTime(e.target.value) })}
              aria-label={t('From')}
            />
            {t('to')}
            <Input
              className="h-8 w-20 tabular-nums"
              placeholder="22:00"
              inputMode="numeric"
              value={p.to}
              invalid={!isTime(p.to) || p.to < p.from}
              onChange={(e) => update(i, { to: e.target.value })}
              onBlur={(e) => update(i, { to: normaliseTime(e.target.value) })}
              aria-label={t('to')}
            />
            {t('every')}
            <Input
              type="number"
              min={1}
              className="h-8 w-16"
              value={p.every}
              onChange={(e) => update(i, { every: Number(e.target.value) })}
              aria-label={t('every')}
            />
            {t('min')}
            <span className="text-xs text-slate-400">
              (
              {tn(
                isTime(p.from) && isTime(p.to) && p.to >= p.from && p.every > 0 ? expandPattern(p).length : 0,
                '{n} bus',
                '{n} buses',
              )}
              )
            </span>
            <button
              type="button"
              onClick={() => onChange(patterns.filter((_, j) => j !== i))}
              className="text-slate-400 hover:text-red-600"
              aria-label={t('Remove')}
            >
              <X className="size-4" />
            </button>
          </div>
        ))}
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        <Button
          size="sm"
          variant="secondary"
          icon={<Plus className="size-4" />}
          onClick={() => {
            const prev = patterns.at(-1);
            const from = prev && isTime(prev.to) ? prev.to : '06:00';
            const to = prev ? fromMinutes(Math.min(toMinutes(from) + 180, 23 * 60 + 59)) : '22:00';
            onChange([...patterns, { from, to, every: prev?.every ?? 15 }]);
          }}
        >
          {t('Add rule')}
        </Button>
        {patterns.length > 0 && (
          <Button size="sm" disabled={!valid} onClick={onApply}>
            {t('Fill departures from the rules')}
          </Button>
        )}
      </div>
    </div>
  );
}

/** Stations down, departures across (like a printed timetable); new times highlighted. */
function ResultGrid({
  result,
  day,
  departures,
}: {
  result: Array<{
    station: { _id: string; name: string };
    minutes: number;
    after: Record<Day, string[]>;
    before: Record<Day, string[]>;
    diff: Record<Day, { added: string[]; removed: string[] }>;
  }>;
  day: Day;
  departures: string[];
}) {
  if (!departures.length)
    return <p className="px-4 py-6 text-sm text-slate-500">{t('No departures yet for these days.')}</p>;
  const trips = [...departures].sort();
  return (
    <div className="max-h-[520px] overflow-auto">
      <table className="text-xs tabular-nums">
        <thead className="sticky top-0 z-10 bg-white">
          <tr>
            <th className="sticky left-0 z-20 bg-white px-4 py-2 text-left font-medium text-slate-500">
              {t('Station')}
            </th>
            <th className="px-2 py-2 text-left font-medium text-slate-500">{t('Changes')}</th>
            {trips.map((d, i) => (
              <th key={d} className="px-1.5 py-2 font-medium text-slate-400">
                {i + 1}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {result.map((r) => {
            const added = new Set(r.diff[day].added);
            const d = r.diff[day];
            return (
              <tr key={r.station._id} className="hover:bg-slate-50">
                <td className="sticky left-0 z-10 max-w-56 truncate bg-white px-4 py-1.5 text-sm text-slate-800">
                  {r.station.name}
                </td>
                <td className="px-2 py-1.5 whitespace-nowrap">
                  {d.added.length + d.removed.length === 0 ? (
                    <span className="text-slate-400">—</span>
                  ) : (
                    <span title={d.removed.length ? `${t('removed')}: ${d.removed.join(' ')}` : undefined}>
                      <span className="text-emerald-700">+{d.added.length}</span>{' '}
                      <span className="text-red-600">−{d.removed.length}</span>
                    </span>
                  )}
                </td>
                {trips.map((dep) => {
                  const time = stationTimes([dep], r.minutes)[0]!;
                  return (
                    <td
                      key={dep}
                      className={`px-1.5 py-1.5 text-center ${added.has(time) ? 'bg-emerald-50 text-emerald-800' : 'text-slate-700'}`}
                    >
                      {time}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
